import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, Send, Loader2, MessageSquare, Lightbulb } from "lucide-react";
import { GlassCard } from "@/components/ui/GlassCard";
import { NxButton } from "@/components/ui/NxButton";
import { apiPost } from "@/lib/api";
import pt from "@/lib/i18n";

export const AskNexusTelWidget = () => {
    const [question, setQuestion] = useState("");
    const [history, setHistory] = useState([]); // [{q, a, ts}]
    const [loading, setLoading] = useState(false);

    const ask = async (q) => {
        const finalQ = (q || question).trim();
        if (!finalQ) return;
        setLoading(true);
        setQuestion("");
        try {
            const r = await apiPost("/nexustel/ask", { question: finalQ });
            setHistory((prev) => [{ q: finalQ, a: r.answer, ts: r.generated_at }, ...prev].slice(0, 6));
        } catch (e) {
            setHistory((prev) => [{ q: finalQ, a: pt.askNexustelError || "Não foi possível processar a pergunta neste momento.", ts: new Date().toISOString() }, ...prev]);
        }
        setLoading(false);
    };

    return (
        <GlassCard
            padding="p-6"
            data-testid="ask-nexustel-widget"
            style={{
                borderColor: "var(--copper)",
                borderWidth: 1.5,
                background: "linear-gradient(135deg, rgba(31,45,64,0.92) 0%, rgba(45,60,80,0.92) 100%)",
            }}
        >
            <div className="flex items-center gap-2 mb-1">
                <div className="relative">
                    <Sparkles size={18} className="text-[color:var(--copper-light)]" />
                </div>
                <h3 className="font-display text-lg text-white tracking-tight">{pt.nexustel.title}</h3>
            </div>
            <p className="font-body text-xs text-white/60 mb-4">{pt.nexustel.subtitle}</p>

            <div className="flex gap-2 mb-3">
                <input
                    type="text"
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !loading) ask(); }}
                    placeholder={pt.nexustel.placeholder}
                    className="flex-1 px-4 py-2.5 rounded-lg bg-white/10 border border-white/20 text-white placeholder:text-white/40 font-body text-sm focus:outline-none focus:border-[color:var(--copper)] focus:bg-white/15 transition-all"
                    disabled={loading}
                    data-testid="ask-input"
                />
                <NxButton
                    variant="copper"
                    onClick={() => ask()}
                    disabled={!question.trim() || loading}
                    icon={loading ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                    data-testid="btn-ask"
                >
                    {loading ? pt.nexustel.thinking : pt.nexustel.ask}
                </NxButton>
            </div>

            {history.length === 0 && (
                <div className="mt-2" data-testid="ask-suggestions">
                    <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-white/40 mb-2 flex items-center gap-1">
                        <Lightbulb size={11} /> {pt.nexustel.suggestionsLabel || "Sugestões"}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                        {pt.nexustel.suggestions.map((s, i) => (
                            <button
                                key={i}
                                onClick={() => ask(s)}
                                disabled={loading}
                                className="px-3 py-1 rounded-full text-xs text-white/80 bg-white/8 hover:bg-[color:var(--copper)]/30 hover:text-white border border-white/15 transition-all font-body"
                                data-testid={`suggestion-${i}`}
                            >
                                {s}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            <AnimatePresence>
                {history.map((h, i) => (
                    <motion.div
                        key={h.ts}
                        initial={{ opacity: 0, y: -6, height: 0 }}
                        animate={{ opacity: 1, y: 0, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        className="mt-3 pt-3 border-t border-white/10"
                        data-testid={`ask-history-${i}`}
                    >
                        <p className="font-body text-xs text-white/50 mb-1.5 flex items-center gap-1.5">
                            <MessageSquare size={11} />{h.q}
                        </p>
                        <p className="font-body text-sm text-white leading-relaxed">{h.a}</p>
                    </motion.div>
                ))}
            </AnimatePresence>
        </GlassCard>
    );
};

export default AskNexusTelWidget;
